// feature/bot-litisoft — sprint90 Phase 3. Admin cockpit · Configuración.
// Set/rotate the PREVISORA credentials (stored AES-256-GCM in Athena; NEVER
// rendered back — only "set ✓ / last updated"), and download the secret-free
// worker bundle. Admin-only (route + sidebar gated).
import React, { useEffect, useState } from "react";
import { bot } from "../../api/endpoints";
import { api } from "../../api/client";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { useToast } from "../../components/Toast";

const fmt = (d) => (d ? new Date(d).toLocaleString("es-CO") : "—");

export default function BotLitisoftConfig() {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const [workers, setWorkers] = useState(null);

  function load() {
    bot.credentialsStatus().then((r) => setStatus(r.data)).catch(() => setStatus({}));
  }
  function loadWorkers() {
    bot.workers().then((r) => setWorkers(r.data.workers)).catch(() => setWorkers([]));
  }
  async function toggleWorker(w) {
    try { await bot.setWorkerRevoked(w.id, !w.revoked); loadWorkers(); }
    catch (err) { toast.push(err?.response?.data?.error || "Error", "error"); }
  }
  useEffect(() => { load(); loadWorkers(); }, []);

  async function save(e) {
    e.preventDefault();
    if (!user.trim() && !pass.trim()) { toast.push("Nada que guardar", "error"); return; }
    setBusy(true);
    try {
      const body = {};
      if (user.trim()) body.previsora_user = user.trim();
      if (pass.trim()) body.previsora_pass = pass.trim();
      await bot.setCredentials(body);
      toast.push("Credenciales guardadas (cifradas)");
      setUser(""); setPass(""); load();
    } catch (err) {
      toast.push(err?.response?.data?.error || "Error", "error");
    } finally {
      setBusy(false);
    }
  }

  async function downloadWorker() {
    setDl(true);
    try {
      const r = await api.get("/api/bot/worker-bundle", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "athena-bot-worker.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.push("Worker descargado");
    } catch (err) {
      toast.push("No se pudo generar el worker", "error");
    } finally {
      setDl(false);
    }
  }

  const s = status || {};
  return (
    <div className="col" style={{ gap: 16, maxWidth: 720 }}>
      <div className="muted">
        Lo que necesito para trabajar: mis credenciales de PREVISORA, el worker que mueve el
        navegador, y un repaso de qué máquinas tengo emparejadas.
      </div>

      <h2 style={{ margin: "8px 0 0", fontSize: 15 }}>Credenciales PREVISORA</h2>
      <div className="muted" style={{ marginTop: -8 }}>
        Se guardan cifradas (AES-256-GCM) en Athena y se entregan al worker en tiempo de
        ejecución. Nunca se muestran de vuelta; deja un campo vacío para no cambiarlo.
      </div>
      <div className="card" style={{ padding: 16 }}>
        {status == null ? (
          <Spinner size={18} />
        ) : (
          <div className="col" style={{ gap: 4, marginBottom: 12 }}>
            <div>
              Usuario: <strong>{s.previsora_user?.set ? "configurado ✓" : "sin configurar"}</strong>
              {s.previsora_user?.set && (
                <span className="muted" style={{ fontSize: 12 }}> · {fmt(s.previsora_user.updated_at)} · {s.previsora_user.updated_by || "—"}</span>
              )}
            </div>
            <div>
              Contraseña: <strong>{s.previsora_pass?.set ? "configurada ✓" : "sin configurar"}</strong>
              {s.previsora_pass?.set && (
                <span className="muted" style={{ fontSize: 12 }}> · {fmt(s.previsora_pass.updated_at)}</span>
              )}
            </div>
            {s.vault_configured === false && (
              <div style={{ color: "var(--color-danger)", fontSize: 12 }}>⚠ BOT_CRED_KEY no está configurado en el servidor.</div>
            )}
          </div>
        )}
        <form className="col" style={{ gap: 8 }} onSubmit={save} autoComplete="off">
          <label style={{ fontSize: 12 }}>Usuario PREVISORA</label>
          <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" placeholder="(dejar vacío para no cambiar)" />
          <label style={{ fontSize: 12 }}>Contraseña PREVISORA</label>
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoComplete="new-password" placeholder="(dejar vacío para no cambiar)" />
          <div><button disabled={busy} type="submit">{busy && <Spinner color="currentColor" />} Guardar / Rotar</button></div>
        </form>
      </div>

      <h2 style={{ margin: "8px 0 0" }}>Descargar worker</h2>
      <div className="muted" style={{ marginTop: -8 }}>
        Paquete del bot configurado para esta instancia de Athena. No contiene secretos:
        trae solo un pairing token de corta duración (ligado a tu cuenta) que el worker
        canjea por su propio token al arrancar.
      </div>
      <div>
        <button className="secondary" disabled={dl} onClick={downloadWorker}>
          {dl && <Spinner color="currentColor" />} Descargar worker (.zip)
        </button>
      </div>

      <h2 style={{ margin: "8px 0 0" }}>Workers emparejados</h2>
      <div className="muted" style={{ marginTop: -8 }}>
        Revocar inutiliza al instante el token de ese worker (se rechaza en su siguiente
        llamada), sin rotar llaves.
      </div>
      <div className="card" style={{ padding: 0 }}>
        {workers == null ? (
          <div style={{ padding: 24, textAlign: "center" }}><Spinner size={18} /></div>
        ) : !workers.length ? (
          <Empty title="Ningún worker emparejado" />
        ) : (
          <table>
            <thead><tr><th>Worker</th><th>Emparejado por</th><th>Última actividad</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {workers.map((w) => (
                <tr key={w.id}>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{w.id}</td>
                  <td>{w.paired_by_name || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmt(w.last_seen_at)}</td>
                  <td>{w.revoked ? <span style={{ color: "var(--color-danger)" }}>revocado</span> : "activo"}</td>
                  <td>
                    {w.revoked
                      ? <button className="ghost sm" onClick={() => toggleWorker(w)}>Reactivar</button>
                      : <button className="ghost sm" style={{ color: "var(--color-danger)" }} onClick={() => toggleWorker(w)}>Revocar</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
